import * as React from 'react';
import { makeStyles, tokens, Field, Label, Text, Input, Link } from '@fluentui/react-components';
import { useNavigation } from '../hooks/routing/AppSpecificRouting';
import { useClient } from '../hooks/client';
import { LoadingState, LoadingButton } from '../components/LoadingButton';
import { Trans, useTranslation } from 'react-i18next';
import { InvalidSystemError } from '../hooks/client/types/ClientTypes';

const useStyles = makeStyles({
  root: {
    paddingInline: tokens.spacingHorizontalL,
    paddingBlockStart: tokens.spacingVerticalXXL,
    paddingBlockEnd: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    justifyContent: 'space-between',
    height: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  labelWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase400,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    textAlign: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// Non-strict regex to allow any 3 part URL, this is formatted to remove paths,
// query params, and add the https protocol if missing.
const URL_REGEX = /^(?:https?:\/\/)?([a-zA-Z0-9-]+\.){2,}[a-zA-Z]{2,}(?:\/[^\s]*)?$/;

export const LoginScreen = () => {
  const { navigate } = useNavigation<'welcome'>();
  const { initiateAuth, enterpriseUrl } = useClient();
  const styles = useStyles();
  const [url, setUrl] = React.useState(enterpriseUrl ?? '');
  const [errorMsg, setErrorMsg] = React.useState<string>();
  const { t } = useTranslation();

  const [loadingState, setLoadingState] = React.useState<LoadingState>('initial');

  const handleLogin = async () => {
    try {
      setLoadingState('loading');
      setErrorMsg('');
      const success = await initiateAuth(url);
      if (success) {
        navigate('main');
        return;
      }
    } catch (e) {
      const errorMsg = e instanceof InvalidSystemError ? t('LOGIN.INVALID_SYSTEM') : t('LOGIN.ERROR');
      setErrorMsg(errorMsg);
    } finally {
      setLoadingState('initial');
    }
  };

  const isValidUrl = new RegExp(URL_REGEX).exec(url)?.length > 0;

  return (
    <div className={styles.root}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleLogin();
        }}
      >
        <div className={styles.labelWrapper}>
          <Label
            className={styles.label}
            required
          >
            {t('LOGIN.LABEL')}
          </Label>
          <Text className={styles.subtitle}>{t('LOGIN.DESCRIPTION')}</Text>
        </div>
        <Field validationMessage={errorMsg}>
          <Input
            placeholder={t('LOGIN.PLACEHOLDER')}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setErrorMsg('');
            }}
          />
        </Field>
        <LoadingButton
          loadingState={loadingState}
          disabled={!isValidUrl}
          appearance="primary"
          type="submit"
        >
          {t('LOGIN.CONNECT')}
        </LoadingButton>
      </form>
      <div className={styles.footer}>
        <Trans
          i18nKey="LOGIN.CONTACT_US"
          components={[
            <Link
              key={0}
              as="a"
              target="_blank"
              rel="noreferrer"
              href="https://www.consultationmanager.com/contact"
            />,
          ]}
        />
      </div>
    </div>
  );
};
